import { useEffect, useState } from 'react';

declare global {
  interface Window {
    // google type is imported from tsconfig.
    google: typeof google;
  }
}

const GOOGLE_IDENTITY_SCRIPT_SELECTOR = 'script[src^="https://accounts.google.com/gsi/client"]';

/**
 * Checks whether the Google Identity Services client can be used right now.
 * @returns True once the GIS script has run and exposed its client.
 */
const isGoogleIdentityLoaded = (): boolean => Boolean(window.google?.accounts?.id);

/**
 * Tracks the Google Identity Services client. Its script tag is loaded asynchronously, so
 * it can run after React mounted: initializing Google on mount alone silently misses it
 * and leaves the sign-in button and One Tap out for the whole page life.
 * @returns True once `window.google.accounts.id` can be used.
 */
export const useGoogleIdentityLoaded = (): boolean => {
  const [isLoaded, setIsLoaded] = useState(isGoogleIdentityLoaded);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    // The script can have run between the first render and this effect.
    if (isGoogleIdentityLoaded()) {
      setIsLoaded(true);

      return;
    }

    const script = document.querySelector(GOOGLE_IDENTITY_SCRIPT_SELECTOR);

    if (!script) {
      return;
    }

    /**
     * Publishes the client availability once its script has run.
     * @returns Nothing.
     */
    const onScriptLoad = (): void => {
      setIsLoaded(isGoogleIdentityLoaded());
    };

    script.addEventListener('load', onScriptLoad);

    return () => {
      script.removeEventListener('load', onScriptLoad);
    };
  }, [isLoaded]);

  return isLoaded;
};
